import { AdminAffiliatesResponse, AffiliateAccountStatus, AffiliateAccountWithUser } from '@fsp/types';
import {
  Box,
  Chip,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaperSection from '../../../components/PaperSection';
import { apiClient } from '../../../services/apiClient';
import { formatDate, DateFormatOptions } from '@fsp/shared';

type StatusFilter = 'ALL' | AffiliateAccountStatus;

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Suspended', value: 'SUSPENDED' },
];

const getStatusColor = (
  status: AffiliateAccountStatus
): 'warning' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'success';
    case 'SUSPENDED':
      return 'error';
    default:
      return 'default';
  }
};

export default function AuditorsList() {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState<AffiliateAccountWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/admin/auditors');

      if (!response.ok) {
        throw new Error('Failed to fetch affiliates');
      }

      const data: AdminAffiliatesResponse = await response.json();
      setAffiliates(data.affiliates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching affiliates:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAffiliates = useMemo(() => {
    if (statusFilter === 'ALL') return affiliates;
    return affiliates.filter((a) => a.status === statusFilter);
  }, [affiliates, statusFilter]);

  const countByStatus = (status: AffiliateAccountStatus) =>
    affiliates.filter((a) => a.status === status).length;

  return (
    <PaperSection title="Affiliates">
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Tabs
        value={statusFilter}
        onChange={(_, val) => setStatusFilter(val as StatusFilter)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {STATUS_TABS.map((tab) => (
          <Tab
            key={tab.value}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {tab.label}
                {tab.value !== 'ALL' && (
                  <Chip
                    label={countByStatus(tab.value as AffiliateAccountStatus)}
                    size="small"
                    color={getStatusColor(tab.value as AffiliateAccountStatus)}
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            }
            value={tab.value}
          />
        ))}
      </Tabs>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Business Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Referral Code</TableCell>
              <TableCell align="right">Clients</TableCell>
              <TableCell>Registered</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={72} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                  <TableCell align="right"><Skeleton variant="text" width={24} sx={{ ml: 'auto' }} /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                </TableRow>
              ))
            ) : filteredAffiliates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No affiliates found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAffiliates.map((affiliate) => (
                  <TableRow
                    key={affiliate.id}
                    onClick={() => navigate(`/app/auditors/${affiliate.id}`)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <TableCell>
                      {affiliate.user.firstName} {affiliate.user.lastName}
                    </TableCell>
                    <TableCell>{affiliate.businessName || '-'}</TableCell>
                    <TableCell>{affiliate.user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={affiliate.status}
                        color={getStatusColor(affiliate.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {affiliate.referralCode}
                    </TableCell>
                    <TableCell align="right">{affiliate.clientCount}</TableCell>
                    <TableCell>
                      {formatDate(affiliate.createdAt, DateFormatOptions.DATEONLY)}
                    </TableCell>
                  </TableRow>
                ))
              )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && filteredAffiliates.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing {filteredAffiliates.length} affiliate{filteredAffiliates.length !== 1 ? 's' : ''}
          {statusFilter !== 'ALL' && ` (${statusFilter.toLowerCase()})`}
        </Typography>
      )}
    </PaperSection>
  );
}
